import { useState, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  Spinner,
  Table,
  Alert
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuthContext } from '../../providers/AuthProvider.js';
import {
  useDrums,
  useCreateDrum,
  useUpdateDrum,
  useDeleteDrum,
  useSeedDrums
} from '../../features/drums/hooks/useDrums.js';
import DrumFormModal from '../../components/drums/DrumFormModal.js';
import DeleteDrumModal from '../../components/drums/DeleteDrumModal.js';

const TYPE_COLORS = {
  metal: 'secondary',
  plastic: 'info',
  wood: 'warning'
};

const DrumList = () => {
  const [authState] = useAuthContext();
  const isAdmin = authState.user?.role === 'admin';

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [formModal, setFormModal] = useState({ show: false, drum: null });
  const [deleteModal, setDeleteModal] = useState({ show: false, drum: null });

  const { data, isLoading } = useDrums();
  const createMutation = useCreateDrum();
  const updateMutation = useUpdateDrum();
  const deleteMutation = useDeleteDrum();
  const seedMutation = useSeedDrums();

  const drums = data?.data || [];

  const filtered = useMemo(() => {
    return drums.filter(d => {
      if (!showInactive && !d.active) return false;
      if (typeFilter && d.drumType !== typeFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const name = (d.name || '').toLowerCase();
        const type = d.drumType.toLowerCase();
        if (!name.includes(q) && !type.includes(q) && !String(d.drumDiameter).includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [drums, search, typeFilter, showInactive]);

  const handleFormSubmit = async payload => {
    if (formModal.drum) {
      await updateMutation.mutateAsync(payload);
    } else {
      await createMutation.mutateAsync(payload);
    }
    setFormModal({ show: false, drum: null });
  };

  const handleDelete = async id => {
    await deleteMutation.mutateAsync(id);
    setDeleteModal({ show: false, drum: null });
  };

  const handleSeed = async () => {
    await seedMutation.mutateAsync();
  };

  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="mb-9 mt-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Drums</h2>
          <p className="text-700 mb-0">
            Manage drum inventory — dimensions used for cable length calculations
          </p>
        </div>
        <div className="d-flex gap-2">
          {isAdmin && (
            <Button
              variant="outline-secondary"
              onClick={handleSeed}
              disabled={seedMutation.isPending}
            >
              {seedMutation.isPending ? 'Seeding...' : 'Seed Defaults'}
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="primary"
              onClick={() => setFormModal({ show: true, drum: null })}
            >
              <FontAwesomeIcon icon="plus" className="me-2" />
              Add Drum
            </Button>
          )}
        </div>
      </div>

      {seedMutation.isSuccess && (
        <Alert variant="success" dismissible className="mb-3">
          {seedMutation.data?.message || 'Default drums seeded successfully'}
        </Alert>
      )}

      {/* Filter bar */}
      <Card className="mb-4">
        <Card.Body className="py-3">
          <Row className="g-3 align-items-center">
            <Col xs={12} sm={4} md={3}>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Search drums..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </Col>
            <Col xs="auto">
              <Form.Select
                size="sm"
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
              >
                <option value="">All types</option>
                <option value="metal">Metal</option>
                <option value="plastic">Plastic</option>
                <option value="wood">Wood</option>
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Form.Check
                type="switch"
                id="show-inactive-drums"
                label="Show inactive"
                checked={showInactive}
                onChange={e => setShowInactive(e.target.checked)}
              />
            </Col>
            <Col xs="auto" className="ms-auto">
              <span className="text-700 fs-9">{filtered.length} drums</span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {isLoading ? (
        <div className="d-flex align-items-center justify-content-center py-6">
          <Spinner
            animation="border"
            variant="primary"
            size="sm"
            className="me-2"
          />
          <span className="text-700">Loading...</span>
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <p className="text-700 mb-2">No drums found</p>
            {drums.length === 0 && isAdmin && (
              <p className="text-700 fs-9">
                Click "Seed Defaults" to add the standard drum set, or "Add Drum" to
                create one manually.
              </p>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table className="table-sm fs-9 mb-0" hover>
                <thead>
                  <tr>
                    <th className="text-800">Name</th>
                    <th className="text-800">Type</th>
                    <th className="text-800 text-end">Drum Diameter (D)</th>
                    <th className="text-800 text-end">Flange Width (B)</th>
                    <th className="text-800 text-end">Barrel Diameter (d)</th>
                    <th className="text-800 text-end">Max Weight</th>
                    <th className="text-800">Status</th>
                    {isAdmin && <th className="text-800 text-end">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(drum => (
                    <tr
                      key={drum._id}
                      className={!drum.active ? 'opacity-50' : ''}
                    >
                      <td className="fw-semibold">{drum.name}</td>
                      <td>
                        <Badge bg={TYPE_COLORS[drum.drumType] || 'dark'}>
                          {drum.drumType.charAt(0).toUpperCase() +
                            drum.drumType.slice(1)}
                        </Badge>
                      </td>
                      <td className="text-end">{drum.drumDiameter} mm</td>
                      <td className="text-end">{drum.flangeWidth} mm</td>
                      <td className="text-end">{drum.barrelDiameter} mm</td>
                      <td className="text-end">
                        {drum.maxWeight ? `${drum.maxWeight} kg` : '-'}
                      </td>
                      <td>
                        <Badge bg={drum.active ? 'success' : 'secondary'}>
                          {drum.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      {isAdmin && (
                        <td className="text-end">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 me-2 text-primary"
                            onClick={() =>
                              setFormModal({ show: true, drum })
                            }
                          >
                            <FontAwesomeIcon icon="pen" size="sm" />
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-danger"
                            onClick={() =>
                              setDeleteModal({ show: true, drum })
                            }
                          >
                            <FontAwesomeIcon icon="trash" size="sm" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      <DrumFormModal
        show={formModal.show}
        onHide={() => setFormModal({ show: false, drum: null })}
        drum={formModal.drum}
        onSubmit={handleFormSubmit}
        isLoading={isMutating}
      />

      <DeleteDrumModal
        show={deleteModal.show}
        onHide={() => setDeleteModal({ show: false, drum: null })}
        drum={deleteModal.drum}
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

export default DrumList;
